import React from "react";
import { motion } from "framer-motion";
import { useI18n } from "../../i18n";
import { GALLERY } from "../../data";

const reveal = {
  hidden: { opacity: 0, y: 36 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export function Marquee() {
  const { t } = useI18n();
  const items = [...t.marquee, ...t.marquee];
  return (
    <section className="relative border-y border-white/10 bg-[#131311] py-6 overflow-hidden" data-testid="marquee">
      <div className="marquee-track">
        {items.map((w, i) => (
          <span key={i} className="flex items-center">
            <span className="font-display italic text-2xl md:text-4xl text-cream/90 px-8">{w}</span>
            <span className="text-lime text-2xl md:text-3xl">✦</span>
          </span>
        ))}
      </div>
    </section>
  );
}

export function Manifesto() {
  const { t } = useI18n();
  return (
    <section className="relative bg-[#0b0b0a] py-28 md:py-40" data-testid="manifesto">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <motion.p
          variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
          className="font-body text-[11px] uppercase tracking-[0.4em] text-lime mb-16"
        >
          {t.manifesto.kicker}
        </motion.p>
        <div className="space-y-2">
          {t.manifesto.chapters.map((c, i) => (
            <motion.div
              key={i}
              variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
              className="group grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 items-start border-t border-white/10 py-10 md:py-14 hover:border-lime/40 transition-colors duration-500"
            >
              <div className="md:col-span-2 font-body text-lime text-sm tracking-widest">{c.n}</div>
              <h3 className="md:col-span-6 font-display font-light text-[clamp(1.9rem,5vw,4rem)] leading-[1] tracking-[-0.02em] text-cream group-hover:translate-x-2 transition-transform duration-500">
                {c.t}
              </h3>
              <p className="md:col-span-4 font-body text-cream/60 leading-relaxed text-base md:pt-3">{c.d}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Services() {
  const { t } = useI18n();
  return (
    <section id="services" className="relative bg-[var(--paper)] text-[#0b0b0a] py-28 md:py-40" data-testid="services">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <motion.p variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} className="font-body text-[11px] uppercase tracking-[0.4em] text-[#0b0b0a]/50 mb-5">
              {t.services.kicker}
            </motion.p>
            <motion.h2 variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} className="font-display font-light text-[clamp(2.6rem,7vw,5.5rem)] leading-[0.95] tracking-[-0.02em]">
              {t.services.title}
            </motion.h2>
          </div>
          <motion.p variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} className="font-body text-[#0b0b0a]/60 max-w-[34ch] text-base">
            {t.services.lead}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {t.services.items.map((s, i) => (
            <motion.div
              key={i}
              variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }}
              className="group relative border-t border-[#0b0b0a]/15 py-9 md:pr-8 md:[&:nth-child(3n+2)]:px-8 lg:[&:nth-child(3n+2)]:border-x lg:[&:nth-child(3n+2)]:border-[#0b0b0a]/15 hover:bg-[#0b0b0a] hover:text-[var(--paper)] transition-colors duration-500 md:px-6 rounded-lg"
            >
              <div className="flex items-start justify-between mb-6">
                <span className="font-body text-xs tracking-widest opacity-50">{s.n}</span>
                <span className="w-2 h-2 rounded-full bg-[#0b0b0a] group-hover:bg-lime transition-colors" />
              </div>
              <h3 className="font-display text-2xl md:text-3xl mb-3">{s.t}</h3>
              <p className="font-body text-sm leading-relaxed opacity-70">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Gallery() {
  const { t } = useI18n();
  return (
    <section id="work" className="relative bg-[#0b0b0a] py-28 md:py-40" data-testid="gallery">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <motion.p variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} className="font-body text-[11px] uppercase tracking-[0.4em] text-lime mb-5">
              {t.gallery.kicker}
            </motion.p>
            <motion.h2 variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} className="font-display font-light text-[clamp(2.6rem,7vw,5.5rem)] leading-[0.95] tracking-[-0.02em] text-cream">
              {t.gallery.title}
            </motion.h2>
          </div>
          <motion.p variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} className="font-body text-cream/50 max-w-[34ch] text-base">
            {t.gallery.lead}
          </motion.p>
        </div>

        <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
          {GALLERY.map((g, i) => (
            <motion.figure
              key={i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: (i % 3) * 0.08 }}
              className="group relative mb-5 break-inside-avoid overflow-hidden rounded-xl bg-[#131311]"
            >
              <img
                src={g.src}
                alt={`Carpe Diem lavoro ${i + 1}`}
                loading="lazy"
                className={`w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06] ${g.tall ? "aspect-[3/4]" : "aspect-[4/5]"}`}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0b0b0a]/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <figcaption className="absolute left-4 bottom-4 translate-y-3 opacity-0 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                <span className="font-script text-lime text-2xl">Carpe Diem</span>
                <span className="block font-body text-[10px] uppercase tracking-[0.3em] text-cream/70">by Anna · {String(i + 1).padStart(2, "0")}</span>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}
